/*!

=========================================================
* Paper Kit React - v1.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-kit-react

* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/paper-kit-react/blob/main/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useRef } from "react";

// reactstrap components
import { Button, Container } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import BrandMark from "../BrandMark/BrandMark";
import useRafScroll from "motion/useRafScroll";

import heroImg from "assets/img/stock/hero-family.jpg";
import heroImgMobile from "assets/img/stock/hero-family-mobile.jpg";

// How far the photograph and the motto travel, as a share of how far the reader
// has. The photo barely moves and the motto lags well behind the page; the gap
// between the two is the whole effect. Props rather than constants so the motion
// lab can stand three headers side by side at different rates.
const PARALLAX_RATIO = 0.18;
const MOTTO_RATIO = 0.55;
// The motto is gone by this share of the header's height, well before the header
// itself leaves. A headline still legible as the next section arrives reads as
// something that failed to clear rather than as depth.
const MOTTO_FADE_END = 0.6;

function GeneHeader({
  parallax = PARALLAX_RATIO,
  mottoRatio = MOTTO_RATIO,
  fadeEnd = MOTTO_FADE_END,
}) {
  const { t } = useTranslation();
  const headerRef = useRef(null);
  // Measured on mount and whenever the viewport changes shape, never per frame.
  // offsetHeight forces a layout, and reading it in the same frame as the custom
  // properties just written would make every scroll frame a synchronous style
  // recalculation.
  const metrics = useRef({ height: 0, viewport: "" });

  useRafScroll((y) => {
    const el = headerRef.current;
    if (!el) return;

    const viewport = `${window.innerWidth}x${window.innerHeight}`;
    if (metrics.current.viewport !== viewport) {
      metrics.current = { viewport, height: el.offsetHeight };
    }
    const height = metrics.current.height || 1;

    // Clamped to the header's own height. Unclamped, the value kept climbing for
    // the whole document, so the photo was still travelling long after the header
    // had left the screen — and returning to the top showed it catching up.
    const depth = Math.min(Math.max(y, 0), height);

    el.style.setProperty("--hero-shift", `${(depth * parallax).toFixed(1)}px`);
    el.style.setProperty("--motto-shift", `${(depth * mottoRatio).toFixed(1)}px`);
    el.style.setProperty(
      "--motto-fade",
      `${Math.max(0, 1 - depth / (height * fadeEnd)).toFixed(3)}`
    );
  });

  return (
    <>
      <div
        ref={headerRef}
        className="gene-header genecolor-bgy75"
        // Only the hashed asset URLs come from here; the tint gradient layered
        // over them lives in .gene-header so the brand hex is defined once, and
        // that rule also decides which crop applies at the current width.
        style={{
          "--hero-image": `url(${heroImg})`,
          "--hero-image-mobile": `url(${heroImgMobile})`,
        }}
      >
        {/* The photograph. This was `className="filter"`, left over from the
            upstream theme, where it paints nothing — `.filter::after` is scoped
            under `.page-header` and this header does not carry that class. It
            was not inert, though: _misc.scss lists `.filter` among the selectors
            given `transition: all 150ms linear`, which on a layer whose
            transform is rewritten every frame would interpolate every parallax
            update and smear the drift into something that reads as a dropped
            frame rather than as depth. */}
        <div className="gene-header-media" aria-hidden="true" />
        <Container>
          <div className="motto text-center">
            {/* The mark leads and writes itself once per tab. It is an image
                above the heading rather than the heading itself: a logotype set
                as the h1 leaves the page's only top-level heading as a picture,
                so the tagline carries that role instead — real visible text
                rather than a hidden string, which is the other way to keep it.

                Only the word is drawn — the navbar carries the full lockup, and
                at this size watching the icon assemble competes with the writing
                rather than leading into it. */}
            {/* Reads as one phrase with the mark under it — "Welcome to
                GeneFlou" — which is what the heading used to say in full before
                the brand became a picture. Kept as its own line rather than
                folded into the mark's alt, so a screen reader hears the greeting
                and the name in the order they are shown. */}
            <p className="hero-welcome">{t("header.welcome")}</p>
            <BrandMark
              className="hero-mark"
              alt="GeneFlou"
              trigger="session"
              variant="wordmark"
            />
            <h1>{t("header.tagline")}</h1>
            {/* Routed, not a bare href="#contactForm". A plain fragment link is
                a navigation the browser makes on its own, pushing a history
                entry the router never learns about — and with restoration taken
                off the browser, going back from one moved the URL and left the
                page where it was. */}
            <Button className="btn-round" tag={Link} to="#contactForm">
              {t("header.cta")}
            </Button>
          </div>
        </Container>
      </div>
    </>
  );
}

export default GeneHeader;
